<template>
    <KFormDisplay
        ref="formDisplayRef"
        id="preanestesibedomning-visa"
        :config-module-name="ConfigModuleName.Operation"
        :config-sub-module-name="OperationConfigModuleName.Preanestesibedomning"
        :showEmptyValues="false"
        formPadding="sections"
        class="preanestesibedomning-visa"
    >
        <template #header>
            <h1>Visa preanestesibedömning</h1>
            <AnmalanInfoBar
                v-if="isNonEmptyObject(operationsanmalan)"
                :anmalan="operationsanmalan"
                :showPreanestesibedömningStatus="true"
                :showUpdated="false"
            />
        </template>
        <template #default>
            <KSection name="operation" heading="Operation" class="operation-section" number="1">
                <Operationsoverblick
                    v-if="preanestesibedomning?.operationsanmalan_display"
                    :anmalan="preanestesibedomning.operationsanmalan_display"
                    @openAnmalan="openOperationsinformation"
                    labelWeight="bold"
                    margin="small"
                />
            </KSection>

            <KSection name="patientdata" heading="Patientdata" class="patient-info-section" number="2">
                <div class="form-content">
                    <div class="form-section span-all" v-if="preanestesibedomning?.neurologisk_sjukdom">
                        <KFormFieldDisplay
                            name="neurologisk_sjukdom"
                            label="Neurologisk sjukdom"
                            :value="preanestesibedomning?.neurologisk_sjukdom"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="neurologisk_sjukdom_text"
                            label="Neurologisk sjukdom beskrivning"
                            :value="preanestesibedomning?.neurologisk_sjukdom_text"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.hjart_karlsjukdom">
                        <KFormFieldDisplay
                            name="hjart_karlsjukdom"
                            label="Hjärt-kärlsjukdom"
                            :value="preanestesibedomning?.hjart_karlsjukdom"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="hjart_karlsjukdom_text"
                            label="Hjärt-kärlsjukdom beskrivning"
                            :value="preanestesibedomning?.hjart_karlsjukdom_text"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.lungsjukdom">
                        <KFormFieldDisplay
                            name="lungsjukdom"
                            label="Lungsjukdom"
                            :value="preanestesibedomning?.lungsjukdom"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="lungsjukdom_text"
                            label="Lungsjukdom beskrivning"
                            :value="preanestesibedomning?.lungsjukdom_text"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.njursjukdom">
                        <KFormFieldDisplay
                            name="njursjukdom"
                            label="Njursjukdom"
                            :value="preanestesibedomning?.njursjukdom"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="njursjukdom_beskrivning"
                            label="Njursjukdom beskrivning"
                            :value="preanestesibedomning?.njursjukdom_beskrivning"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.blodningsbenagenhet">
                        <KFormFieldDisplay
                            name="blodningsbenagenhet"
                            label="Blödningsbenägenhet"
                            :value="preanestesibedomning?.blodningsbenagenhet"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="blodningsbenagenhet_beskrivning"
                            label="Blödningsbenägenhet beskrivning"
                            :value="preanestesibedomning?.blodningsbenagenhet_beskrivning"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.antikoagulantia">
                        <KFormFieldDisplay
                            name="antikoagulantia"
                            label="Antikoagulantia"
                            :value="preanestesibedomning?.antikoagulantia"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="antikoagulantia_beskrivning"
                            label="Antikoagulantia beskrivning"
                            :value="preanestesibedomning?.antikoagulantia_beskrivning"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.overkanslighet">
                        <KFormFieldDisplay
                            name="overkanslighet"
                            label="Överkänslighet"
                            :value="preanestesibedomning?.overkanslighet"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="overkanslighet_text"
                            label="Överkänslig mot"
                            :value="preanestesibedomning?.overkanslighet_text"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.diabetes">
                        <KFormFieldDisplay
                            name="diabetes"
                            label="Diabetes"
                            :value="preanestesibedomning?.diabetes"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="diabetes_lakemedelsform_display"
                            label="Behandlingsform för diabetes"
                            :value="preanestesibedomning?.diabetes_lakemedelsform_display?.namn"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div
                        class="form-section span-all"
                        v-if="preanestesibedomning?.rokare || preanestesibedomning?.snusare"
                    >
                        <KFormFieldDisplay
                            name="rokare"
                            label="Rökare"
                            :value="preanestesibedomning?.rokare_display"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="snusare"
                            label="Snusare"
                            :value="preanestesibedomning?.snusare_display"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div
                        class="form-section span-all"
                        v-if="
                            preanestesibedomning?.anamnes ||
                            preanestesibedomning?.kommentar_anestesi ||
                            preanestesibedomning?.lakemedelsallergi ||
                            preanestesibedomning?.aktuella_lakemedel ||
                            preanestesibedomning?.puls ||
                            preanestesibedomning?.blodtryck_systoliskt ||
                            preanestesibedomning?.blodtryck_diastoliskt
                        "
                    >
                        <KFormFieldDisplay
                            name="anamnes"
                            label="Annan sjukdom, anamnes"
                            :value="preanestesibedomning?.anamnes"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                        <KFormFieldDisplay
                            name="kommentar_anestesi"
                            label="Tidigare anestesi"
                            :value="preanestesibedomning?.kommentar_anestesi"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="lakemedelsallergi"
                            label="Läkemedelsallergi"
                            :value="preanestesibedomning?.lakemedelsallergi"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="aktuella_lakemedel"
                            label="Aktuella läkemedel"
                            :value="preanestesibedomning?.aktuella_lakemedel"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="puls"
                            label="Puls"
                            :value="preanestesibedomning?.puls"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KGroup
                            name="blodtryck"
                            class="span-two"
                            v-if="
                                preanestesibedomning?.blodtryck_systoliskt ||
                                preanestesibedomning?.blodtryck_diastoliskt
                            "
                        >
                            <KFormFieldDisplay
                                name="blodtryck_systoliskt"
                                label="Blodtryck systoliskt"
                                :value="preanestesibedomning?.blodtryck_systoliskt"
                                labelWeight="bold"
                                margin="small"
                            />

                            <KFormFieldDisplay
                                name="blodtryck_diastoliskt"
                                label="Blodtryck diastoliskt"
                                :value="preanestesibedomning?.blodtryck_diastoliskt"
                                labelWeight="bold"
                                margin="small"
                            />
                        </KGroup>
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.langd || preanestesibedomning?.vikt">
                        <KFormFieldDisplay
                            name="langd"
                            label="Längd (cm)"
                            :value="preanestesibedomning?.langd"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="vikt"
                            label="Vikt (kg)"
                            :value="replaceSeparator({ value: preanestesibedomning?.vikt })"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="bmi"
                            label="BMI"
                            class="span-one"
                            :value="bmi"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div class="form-section span-all" v-if="preanestesibedomning?.lab">
                        <KFormFieldDisplay
                            name="anmarkning"
                            label="Lab"
                            :value="preanestesibedomning?.lab_display"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="lab_anmarkning"
                            label="Anmärkning lab"
                            :value="preanestesibedomning?.lab_anmarkning"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div
                        class="form-section span-all"
                        v-if="preanestesibedomning?.senaste_hb || preanestesibedomning?.senaste_hb_datum"
                    >
                        <KFormFieldDisplay
                            name="senaste_hb"
                            label="Senaste HB"
                            :value="preanestesibedomning?.senaste_hb"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="senaste_hb_datum"
                            label="Datum för HB"
                            :value="preanestesibedomning?.senaste_hb_datum"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div
                        class="form-section span-all"
                        v-if="
                            preanestesibedomning?.ekg ||
                            preanestesibedomning?.ekg_datum ||
                            preanestesibedomning?.ekg_anmarkning
                        "
                    >
                        <KFormFieldDisplay
                            name="ekg"
                            label="EKG"
                            :value="preanestesibedomning?.ekg_display"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="ekg_datum"
                            label="Datum för EKG"
                            :value="preanestesibedomning?.ekg_datum"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="ekg_anmarkning"
                            label="Anmärkning EKG"
                            :value="preanestesibedomning?.ekg_anmarkning"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>
                </div>
            </KSection>

            <KSection name="bedomning" heading="Bedömning" class="bedomning-section" number="3">
                <div class="form-content" v-if="preanestesibedomning?.asa || preanestesibedomning?.kommentar_asa">
                    <KFormFieldDisplay
                        name="asa"
                        label="ASA"
                        :value="preanestesibedomning?.asa"
                        labelWeight="bold"
                        margin="small"
                    />

                    <KFormFieldDisplay
                        name="kommentar_asa"
                        label="Kommentar ASA"
                        :value="preanestesibedomning?.kommentar_asa"
                        class="span-two"
                        labelWeight="bold"
                        margin="small"
                    />
                </div>
            </KSection>

            <KSection name="anestesi" heading="Anestesi" class="anestesi-section" number="4">
                <div class="form-content">
                    <div
                        class="form-section span-all"
                        v-if="
                            preanestesibedomning?.anestesiolog_display || preanestesibedomning?.huvudanestesikod_display
                        "
                    >
                        <KFormFieldDisplay
                            name="anestesiolog"
                            label="Anestesiolog"
                            :value="preanestesibedomning?.anestesiolog_display?.user_display?.name"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="huvudanestesikod"
                            label="Huvudanestesikod"
                            :value="preanestesibedomning?.huvudanestesikod_display"
                            class="span-three"
                            labelWeight="bold"
                            margin="small"
                            #default="{ value }"
                        >
                            <span> {{ `${value.code} ${value.displayName}` }} <br /> </span>
                        </KFormFieldDisplay>
                    </div>

                    <KFormFieldDisplay
                        name="bianestesikoder"
                        label="Bianestesikod"
                        :value="preanestesibedomning?.bianestesikoder_display"
                        class="span-all"
                        :content-direction="RenderDirection.Vertical"
                        labelWeight="bold"
                        margin="small"
                        #default="{ value }"
                        v-if="
                            preanestesibedomning?.bianestesikoder_display &&
                            preanestesibedomning?.bianestesikoder_display.length > 0
                        "
                    >
                        <span v-for="item in value" :key="item.id">
                            {{ `${item.code} ${item.displayName}` }} <br />
                        </span>
                    </KFormFieldDisplay>

                    <div
                        class="form-section span-all"
                        v-if="
                            preanestesibedomning?.anestesi_display ||
                            preanestesibedomning?.blockad_display ||
                            preanestesibedomning?.premedicinering_display ||
                            preanestesibedomning?.information_till_koordinator ||
                            preanestesibedomning?.ordinationer_operation ||
                            preanestesibedomning?.ordinationer_avdelning ||
                            preanestesibedomning?.lakemedel_morgon
                        "
                    >
                        <KFormFieldDisplay
                            name="anestesi"
                            label="Anestesi"
                            :value="preanestesibedomning?.anestesi_display?.displayName"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="blockad"
                            label="Blockad"
                            :value="preanestesibedomning?.blockad_display?.displayName"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="premedicinering"
                            label="Premedicinering"
                            :value="preanestesibedomning?.premedicinering_display"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                            #default="{ value }"
                        >
                            <span v-for="item in value" :key="item.id" class="span-all">
                                {{ item.namn }}
                            </span>
                        </KFormFieldDisplay>

                        <KFormFieldDisplay
                            name="information_till_koordinator"
                            label="Information till koordinator"
                            :value="preanestesibedomning?.information_till_koordinator"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        >
                        </KFormFieldDisplay>

                        <KFormFieldDisplay
                            name="ordinationer_operation"
                            label="Information till operation/anestesi"
                            :value="preanestesibedomning?.ordinationer_operation"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="ordinationer_avdelning"
                            label="Information till vårdavdelning"
                            :value="preanestesibedomning?.ordinationer_avdelning"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="lakemedel_morgon"
                            label="Ordinarie läkemedel som SKA ges på operationsdagens morgon"
                            :value="preanestesibedomning?.lakemedel_morgon"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>
                </div>
            </KSection>
            <KSection name="godkannande" heading="Godkännande inför operation" class="godkannande-section" number="5">
                <div class="form-content">
                    <div
                        class="form-section span-all"
                        v-if="
                            preanestesibedomning?.godkannande ||
                            preanestesibedomning?.datum_godkannande ||
                            preanestesibedomning?.giltighetstid
                        "
                    >
                        <KFormFieldDisplay
                            name="godkannande"
                            label="Godkännande"
                            :value="preanestesibedomning?.godkannande"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="datum_godkannande"
                            label="Datum för godkännande"
                            :value="preanestesibedomning?.datum_godkannande"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="giltighetstid"
                            label="Giltighetstid"
                            :value="preanestesibedomning?.giltighetstid"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>

                    <div
                        class="form-section span-all"
                        v-if="
                            canEdit ||
                            preanestesibedomning?.kommentar ||
                            preanestesibedomning?.komplettering_kravd_datum ||
                            preanestesibedomning?.komplettering_utford
                        "
                    >
                        <template v-if="canEdit">
                            <KText
                                label="Kommentar"
                                name="kommentar"
                                type="textarea"
                                :value="formData?.kommentar"
                                @inputValue="formData.kommentar = $event.value"
                                class="span-two"
                                labelWeight="bold"
                                margin="small"
                            />
                        </template>
                        <template v-else>
                            <KFormFieldDisplay
                                name="kommentar"
                                label="Kommentar"
                                :value="preanestesibedomning?.kommentar"
                                class="span-two"
                                labelWeight="bold"
                                margin="small"
                            />
                        </template>

                        <KFormFieldDisplay
                            name="komplettering_kravd_datum"
                            label="Startdatum komplettering krävd"
                            :value="preanestesibedomning?.komplettering_kravd_datum"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KCheckbox
                            ref="kompletteringUtfordRef"
                            name="komplettering_utford"
                            label="Komplettering utförd"
                            valueType="boolean"
                            variant="complete"
                            :value="formData?.komplettering_utford"
                            @inputValue="formData.komplettering_utford = $event.value"
                            labelWeight="bold"
                            margin="small"
                        />

                        <KFormFieldDisplay
                            name="accountableHealthcareProfessional"
                            label="Signeringsansvarig"
                            :value="preanestesibedomning?.accountableHealthcareProfessional_display?.user_display?.name"
                            class="span-two"
                            labelWeight="bold"
                            margin="small"
                        />
                    </div>
                </div>
            </KSection>
        </template>
        <template #actions>
            <ButtonGroup margin="none">
                <KButton type="button" size="primary" @click="saveForm" :disabled="!canEdit">Spara</KButton>
                <KButton type="button" size="primary" @click="openEditPreanestesi" :disabled="!canCreatePreanestesi"
                    >Redigera preanestesibedömning</KButton
                >
                <KButton class="print-button" size="secondary" @click="print" :disabled="hasUnsavedData">
                    Skriv ut
                </KButton>
                <KButton variant="contour" size="secondary" @click="cancelForm()">Avbryt</KButton>
            </ButtonGroup>
        </template>
    </KFormDisplay>
</template>
<script lang="ts">
    import tabIcon from "@/assets/operationsanmalan.svg";
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import KGroup from "@/components/forms/groups/Group/KGroup.vue";

    export default {
        extends: BaseTab,
        name: "PreanestesibedömningVisa",
        icon: tabIcon,
        tabName: "Preanestesibedömning",
        patientBound: true,
    };
</script>

<script setup lang="ts">
    import { ref, computed, toRefs, onMounted, onUnmounted, watch, nextTick } from "vue";
    import { useStore } from "@/store";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import { replaceSeparator } from "@/components/forms/inputs/Number/numberUtils";
    import KButton from "@/components/Button.vue";
    import { getConfig, klinikenApi } from "@/api";
    import { flashMessage, openDialog } from "@/utils";
    import { isNonEmptyObject } from "@/tabs/Operation/utilsTs";
    import emitter from "tiny-emitter/instance";
    import KSection from "@/components/generic/KSection.vue";
    import KCheckbox from "@/components/forms/inputs/Checkbox/KCheckbox.vue";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import Operationsoverblick from "@/tabs/Operation/Operationsoverblick.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import AnmalanInfoBar from "@/tabs/Operation/AnmalanInfoBar.vue";
    import { RenderDirection } from "@/components/forms/types";
    import { ConfigModuleName, OperationConfigModuleName } from "@/store/modules/types/configModuleTypes";
    import KFormDisplay from "@/components/forms/displays/FormDisplay/KFormDisplay.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import cloneDeep from "lodash/cloneDeep";
    import Preanestesibedomning from "@/tabs/Operation/Preanestesibedomning.vue";
    import { printInIframe } from "@/utils/printWindow";
    import moment from "moment";

    const store = useStore();

    const props = defineProps<{
        parameters: { operationsanmalanId: string; preanestesibedomningId: string };
        tabId: number;
    }>();

    const { parameters, tabId } = toRefs(props);

    const preanestesibedomning = ref<any>(null);
    const operationsanmalan = ref<any>(null);

    const formDisplayRef = ref();
    const kompletteringUtfordRef = ref();
    const formData = ref<{
        kommentar: string | undefined | unknown;
        komplettering_utford: boolean | undefined | unknown;
    }>({
        kommentar: undefined,
        komplettering_utford: undefined,
    });
    const formInitialData = ref<{
        kommentar: string | undefined | unknown;
        komplettering_utford: boolean | undefined | unknown;
    }>({
        kommentar: undefined,
        komplettering_utford: undefined,
    });

    const can = store.getters["ability/can"] as (args: { action: string; subject: string }) => boolean;

    const userRole = computed(() => store.getters["userData/currentProfile"]?.roll?.code);

    const godkannande = computed(() => preanestesibedomning.value?.godkannande || "");

    const canKomplettera = computed(() => {
        return can({ action: "komplettera", subject: "preanestesibedomning" });
    });

    const canEdit = computed(() => canKomplettera.value && godkannande.value === "Komplettering krävs");

    const canCreatePreanestesi = computed(() => {
        return userRole.value === "anestesilakare" && can({ action: "create", subject: "preanestesibedomning" });
    });

    const hasUnsavedData = computed(() => {
        return JSON.stringify(formData.value) !== JSON.stringify(formInitialData.value);
    });

    const bmi = computed(() => {
        const vikt = preanestesibedomning.value?.vikt ? Number(preanestesibedomning.value?.vikt) : 0;
        const langd = preanestesibedomning.value?.langd ? Number(preanestesibedomning.value?.langd) : 0;

        if (vikt <= 0 || langd <= 0) {
            return;
        } else {
            const langdInMeters = langd / 100;

            return (vikt / (langdInMeters * langdInMeters)).toFixed(1).replace(".", ",");
        }
    });

    watch(
        canEdit,
        async (value) => {
            await nextTick();
            kompletteringUtfordRef.value?.setDisabled?.({ value: !value });
        },
        { immediate: true }
    );

    // Ökas för varje ny hämtning, så ett inaktuellt svar (t.ex. den initiala hämtningen, om den råkar landa
    // efter en hämtning som triggats av preanestesibedomning-uppdaterad) kan upptäckas och ignoreras istället
    // för att skriva över nyare data.
    let latestFetchId = 0;

    const fetchPreanestesibedomning = async () => {
        const fetchId = ++latestFetchId;
        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        try {
            const config = getConfig({}, "Preanestesibedömning");
            const response = await klinikenApi.get(
                `/operation/preanestesibedomningar/${parameters.value.preanestesibedomningId}/`,
                config
            );

            if (fetchId !== latestFetchId) {
                return;
            }

            preanestesibedomning.value = response.data;
            operationsanmalan.value = preanestesibedomning.value.operationsanmalan_display;

            // Skriv inte över osparade ändringar - denna hämtning kan triggas av att preanestesibedömningen
            // sparats/signerats i en annan öppen flik (preanestesibedomning-uppdaterad), inte bara av att
            // vyn öppnas första gången.
            if (!hasUnsavedData.value) {
                formData.value = {
                    kommentar: preanestesibedomning.value.kommentar || "",
                    komplettering_utford: preanestesibedomning.value.komplettering_utford || false,
                };
                formInitialData.value = cloneDeep(formData.value);
            }
        } catch (e) {
            console.error(e);
            openDialog("Kunde inte hämta preanestesibedömningen.", "error");
        }

        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
    };

    const onPreanestesibedomningUppdaterad = ({ operationsanmalanId }: { operationsanmalanId: string }) => {
        if (operationsanmalanId === parameters.value.operationsanmalanId) {
            fetchPreanestesibedomning();
        }
    };

    onMounted(async () => {
        // Registreras före den initiala hämtningen, så ett event som triggas medan den fortfarande är på väg
        // inte missas.
        emitter.on("preanestesibedomning-uppdaterad", onPreanestesibedomningUppdaterad);
        await fetchPreanestesibedomning();
    });

    onUnmounted(() => {
        emitter.off("preanestesibedomning-uppdaterad", onPreanestesibedomningUppdaterad);
    });

    const saveForm = async () => {
        if (!canEdit.value) {
            return;
        }

        let data = {
            ...formData.value,
            godkannande: formData.value.komplettering_utford
                ? "Komplettering utförd"
                : preanestesibedomning.value.godkannande,
            id: preanestesibedomning.value.id,
            patient: preanestesibedomning.value.patient,
            operationsanmalan: operationsanmalan.value.id,
        };

        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });
        try {
            await store.dispatch("operation/preanestesibedomning/save", data);
            await store.dispatch("operation/operationsanmalanV2/getOperationsanmalanById", {
                id: operationsanmalan.value.id,
                forceRefresh: true,
                addAsUpdate: true,
            });

            flashMessage("Preanestesibedömning uppdaterad");
            store.dispatch("tabs/closeTab", tabId.value);
        } finally {
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    defineExpose({
        hasUnsavedData,
        saveForm,
    });

    const cancelForm = () => {
        if (hasUnsavedData.value) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });
        } else {
            store.dispatch("tabs/closeTab", tabId.value);
        }
    };

    const openEditPreanestesi = async () => {
        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", Preanestesibedomning);
        store.dispatch("tabs/openTab", {
            component: Preanestesibedomning,
            parameters: {
                operationsanmalanId: parameters.value.operationsanmalanId,
                preanestesibedomningId: parameters.value.preanestesibedomningId,
                showPreanestesibedomning: true,
            },
        });
    };

    const print = async () => {
        const el = formDisplayRef.value?.$el as HTMLElement | undefined;
        if (!el) return;

        try {
            await printInIframe({
                url: "/operation/preanestesibedomningar/utskrift/",
                items: [preanestesibedomning.value],
                logName: "Preanestesibedömning",
                contentElement: el,
                fieldDisplayLayout: "columns",
                patientInfo: operationsanmalan.value?.patient_display,
                title: `Preanestesibedömning ${
                    operationsanmalan.value?.patient_display?.personId_formatted || ""
                } ${moment().format("YYYY-MM-DD HH:mm")}`,
            });
        } catch (_) {
            openDialog("Det gick inte att skriva ut.", "error");
        }
    };

    const openOperationsinformation = async () => {
        await store.dispatch("tabs/openTab", {
            component: Operationsinformation,
            parameters: { operationsanmalan_id: operationsanmalan.value.id },
        });
    };
</script>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";
    @import "@/style/components";

    .preanestesibedomning-visa {
    }
</style>
